import React, { useState, useEffect, useCallback } from 'react';
import { Redirect } from "react-router-dom";
import { BetaBadge } from '@/components/ui/BetaBadge';
import { userService } from '../_services';
import { usePopup } from '../hooks/usePopup';
import PagesWrapper from '@/components/ui/shared/PagesWrapper';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Plus, Trash2, Filter } from 'lucide-react';
import CreateNewFilter from './CreateNewFilter';
import EditFilter from './EditFilter';
import DeletePopup from './ConfirmDeletePopup';

function AdvanceFilter({ AnalyticData }) {
    const [userdata] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem('user'));
        } catch {
            return null;
        }
    });
    
    const [filterList, setFilterList] = useState([]);
    const [selectedFilterId, setSelectedFilterId] = useState(0);
    const [selectedFilterName, setSelectedFilterName] = useState('');
    const [selectedFilterData, setSelectedFilterData] = useState([]);
    const [filterIdToDelete, setFilterIdToDelete] = useState('');
    
    const createFilterPopup = usePopup();
    const editFilterPopup = usePopup();
    const deletePopup = usePopup();

    useEffect(() => {
        localStorage.setItem('Page_Path', '/filter');
    }, []);

    useEffect(() => {
        if (userdata?.dList?.[0]?.dashboardId) {
            fetchUserFilters();
        }
    }, [userdata]);

    const fetchUserFilters = useCallback(async () => {
        try {
            const dashboardId = userdata?.dList?.[0]?.dashboardId;
            if (!dashboardId) return;

            const requestOptions = {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json', 
                    'X-Browser-Url': window.location.href 
                },
                body: JSON.stringify({ 
                    FilterName: '', 
                    FilterJson: '', 
                    DashboardId: dashboardId 
                })
            };

            const response = await fetch('/api/Filter/GetUserAdFilter', requestOptions);
            const data = await response.json();
            
            if (data?.[0]?.filterset) {
                setFilterList(data[0].filterset);
            }
        } catch (error) {

        }
    }, [userdata]);

    const handleCreateFilterClose = useCallback(() => {
        createFilterPopup.close();
        fetchUserFilters();
    }, [createFilterPopup, fetchUserFilters]);

    const handleEditFilter = useCallback((filter) => {
        setSelectedFilterId(filter.dashboardId);
        setSelectedFilterName(filter.filterName);
        const filteredData = filterList.filter(f => f.dashboardId === filter.dashboardId);
        setSelectedFilterData(filteredData);
        editFilterPopup.open();
    }, [filterList, editFilterPopup]);

    const handleEditFilterClose = useCallback(() => {
        editFilterPopup.close();
        fetchUserFilters();
    }, [editFilterPopup, fetchUserFilters]);

    const handleDeleteClick = useCallback((e, filterId) => {
        e.stopPropagation();
        setFilterIdToDelete(filterId);
        deletePopup.open();
    }, [deletePopup]);

    const handleDeleteConfirm = useCallback(async () => {
        try {
            const dashboardId = userdata?.dList?.[0]?.dashboardId;
            const requestOptions = {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                    fid: parseInt(filterIdToDelete), 
                    dashid: parseInt(dashboardId), 
                    fname: '' 
                })
            };

            await fetch('/api/Filter/RemovUserAdFilter', requestOptions);
            deletePopup.close();
            fetchUserFilters();
        } catch (error) {

        }
    }, [filterIdToDelete, userdata, deletePopup, fetchUserFilters]);

    if (userService.Check_LoggedIn_Status() === 'Login') {
        return <Redirect to="/login" />;
    }

    return (
        <PagesWrapper>
            <TooltipProvider>
                <div className="space-y-6">
                    <div className="flex items-center">
                        <h1 className="text-2xl font-semibold text-foreground">
                            Manage Filters
                            <BetaBadge />
                        </h1>
                    </div>

                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                        <Button
                            variant="outline"
                            className="h-auto py-4 flex flex-col items-center justify-center border-dashed border-2 hover:border-primary hover:bg-primary/5 min-h-[100px]"
                            onClick={createFilterPopup.open}
                        >
                            <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                                <Plus className="h-4 w-4 text-primary" />
                            </div>
                            <span className="font-medium">Create New Filter</span>
                        </Button>

                        {filterList?.map((filter) => (
                            <Card 
                                key={filter.dashboardId}
                                className="cursor-pointer hover:shadow-md transition-all hover:border-primary/50 group min-h-[100px]"
                                onClick={() => handleEditFilter(filter)}
                            >
                                <CardContent className="p-3 sm:p-4 relative h-full flex items-center">
                                    <div className="flex items-center gap-3 pr-8 flex-1">
                                        <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                                            <Filter className="h-5 w-5 text-primary" />
                                        </div>
                                        <div className="min-w-0">
                                            <p className="font-medium text-foreground truncate">{filter.filterName}</p>
                                            <p className="text-xs text-muted-foreground mt-1">Click to edit</p>
                                        </div>
                                    </div>
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-8 w-8 absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity text-destructive hover:text-destructive hover:bg-destructive/10"
                                                onClick={(e) => handleDeleteClick(e, filter.dashboardId)}
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </Button>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                            <p>Delete this filter</p>
                                        </TooltipContent>
                                    </Tooltip>
                                </CardContent>
                            </Card>
                        ))}

                        {(!filterList || filterList.length === 0) && (
                            <div className="col-span-full text-center py-8 text-muted-foreground">
                                <Filter className="h-10 w-10 mx-auto mb-3 opacity-50" />
                                <p>No filters created yet.</p>
                                <p className="text-sm">Click "Create New Filter" to get started.</p>
                            </div>
                        )}
                    </div>
                </div>
            </TooltipProvider>

            {createFilterPopup.isOpen && (
                <CreateNewFilter calldata="callonce" text="Create New Filter" closePopup={handleCreateFilterClose} />
            )}

            {editFilterPopup.isOpen && (
                <EditFilter calldata="callonce" text={selectedFilterName} binddashid={selectedFilterId} binddashboarddata={selectedFilterData} closePopup={handleEditFilterClose} />
            )}

            {deletePopup.isOpen && (
                <DeletePopup ConfirmSection={true} removeFilter={handleDeleteConfirm} titleText="Are you sure you want to delete this filter?" closePopup={deletePopup.close} />
            )}
        </PagesWrapper>
    );
}

export default AdvanceFilter;
